import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Search, Filter } from "lucide-react";
import PropertyTile from "@/components/marketplace/PropertyTile";
import SearchTile from "@/components/marketplace/SearchTile";
import PropertyFilters from "@/components/marketplace/PropertyFilters";
import SearchFilters from "@/components/marketplace/SearchFilters";
import PropertyTypeChips from "@/components/marketplace/PropertyTypeChips";

// localStorage stores ONLY non-sensitive marketplace filter preferences
// (city, max_price, area, max_budget). Auth uses httpOnly cookies — never
// tokens here. Safe from XSS leakage of credentials.
function readSavedFilters(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
}

export default function Marketplace() {
  const { lang, user } = useApp();
  const STORAGE_KEY = user ? `aaron_mk_filters_${user.user_id}` : "aaron_mk_filters";
  const initial = readSavedFilters(STORAGE_KEY);

  const [props, setProps] = useState([]);
  const [searches, setSearches] = useState([]);
  const [city, setCity] = useState(initial.city || "");
  const [maxPrice, setMaxPrice] = useState(initial.max_price || "");
  const [area, setArea] = useState(initial.area || "");
  const [maxBudget, setMaxBudget] = useState(initial.max_budget || "");
  const [propType, setPropType] = useState(initial.property_type || "");
  const [searchType, setSearchType] = useState(initial.search_property_type || "");
  const [saved, setSaved] = useState(false);

  const load = React.useCallback(() => {
    const params = {};
    if (city) params.city = city;
    if (maxPrice) params.max_price = maxPrice;
    if (propType) params.property_type = propType;
    api.get("/marketplace/properties", { params }).then(({ data }) => setProps(data)).catch(() => {});
  }, [city, maxPrice, propType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = {};
    if (area) params.area = area;
    if (maxBudget) params.max_budget = maxBudget;
    if (searchType) params.property_type = searchType;
    api.get("/marketplace/searches", { params }).then(({ data }) => setSearches(data)).catch(() => {});
  }, [area, maxBudget, searchType]);

  const saveFilters = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      city, max_price: maxPrice, area, max_budget: maxBudget,
      property_type: propType, search_property_type: searchType,
    }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const clearAll = () => {
    setCity("");
    setMaxPrice("");
    setArea("");
    setMaxBudget("");
    setPropType("");
    setSearchType("");
    localStorage.removeItem(STORAGE_KEY);
  };

  const activeFilters = [city, maxPrice, area, maxBudget, propType, searchType].filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="marketplace-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.4em] uppercase text-[#C5A880] mb-2">{t("common_board", lang)}</p>
          <h2 className="text-3xl font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {t("for_sale_headline", lang)}
          </h2>
        </div>
        {activeFilters > 0 && (
          <div
            data-testid="active-filters-chip"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A880]/15 border border-[#C5A880] text-[#8A6E4E]"
          >
            <Filter className="h-3.5 w-3.5" />
            <span className="text-xs tracking-widest uppercase">{t("filters_applied", lang)}</span>
            <span className="text-xs font-mono bg-[#C5A880] text-black rounded-full px-2 py-0.5" data-testid="active-filters-count">
              {activeFilters}
            </span>
          </div>
        )}
      </div>

      <Tabs defaultValue="props">
        <TabsList className="bg-[#FFFAF0] border border-[#D8CFBB] rounded-full p-1">
          <TabsTrigger
            value="props"
            data-testid="mk-tab-props"
            className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black"
          >
            <Building2 className="me-2 h-3.5 w-3.5" />
            {t("for_sale_short", lang)} ({props.length})
          </TabsTrigger>
          <TabsTrigger
            value="searches"
            data-testid="mk-tab-searches"
            className="rounded-full data-[state=active]:bg-[#C5A880] data-[state=active]:text-black"
          >
            <Search className="me-2 h-3.5 w-3.5" />
            {t("searches_tab", lang)} ({searches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="props" className="mt-6 space-y-4">
          <PropertyTypeChips value={propType} onChange={setPropType} testIdPrefix="mk-props-type" />
          <PropertyFilters
            city={city}
            setCity={setCity}
            maxPrice={maxPrice}
            setMaxPrice={setMaxPrice}
            hasAny={!!(city || maxPrice || propType)}
            onReset={() => { setCity(""); setMaxPrice(""); setPropType(""); }}
            onSave={saveFilters}
            saved={saved}
            onClearAll={clearAll}
            showClearAll={!!(city || maxPrice || area || maxBudget || propType || searchType)}
          />
          {!props.length && <p className="text-sm text-[#9A8B72] py-10 text-center">{t("no_data", lang)}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {props.map((p) => <PropertyTile key={p.id} p={p} onRefresh={load} />)}
          </div>
        </TabsContent>

        <TabsContent value="searches" className="mt-6 space-y-4">
          <PropertyTypeChips value={searchType} onChange={setSearchType} testIdPrefix="mk-searches-type" />
          <SearchFilters
            area={area}
            setArea={setArea}
            maxBudget={maxBudget}
            setMaxBudget={setMaxBudget}
            hasAny={!!(area || maxBudget || searchType)}
            onReset={() => { setArea(""); setMaxBudget(""); setSearchType(""); }}
          />
          {!searches.length && <p className="text-sm text-[#9A8B72] py-10 text-center">{t("no_data", lang)}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {searches.map((s) => <SearchTile key={s.id} s={s} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
